import { getModuleRoute } from "../../utils/moduleRoutes";

export function readPurchaseMenus() {
  try {
    const parents = JSON.parse(localStorage.getItem("sidebarItems") || "[]");
    return Array.isArray(parents) ? parents : [];
  } catch (_) { return []; }
}

export function activePurchaseMenus(parents) {
  return parents.filter((parent) => String(parent.status).toLowerCase() === "active")
    .flatMap((parent) => (Array.isArray(parent.child_menu) ? parent.child_menu : [])
      .filter((child) => String(child.status).toLowerCase() === "active")
      .map((child) => ({ ...child, parentName: parent.name })));
}

export function hasPurchaseRight(menus, code, right) {
  return menus.some((menu) => menu.menu_code === code && menu.rights?.[right] === true);
}

export function configuredPurchaseMenus(menus) {
  const seen = new Set();
  return menus.filter((menu) => {
    if (!/^[A-Za-z0-9_]{1,31}$/.test(menu.menu_code || "") || getModuleRoute(menu) ||
        /master|report/i.test(menu.type || "") || (menu.rights?.can_view !== true && menu.rights?.can_add !== true) || seen.has(menu.menu_code)) return false;
    seen.add(menu.menu_code);
    return true;
  });
}

export function validatePurchaseSettings(settings, code, order = false) {
  if (settings?.tran_code !== code || !settings.type || settings.type === "master" || getModuleRoute(settings) || !settings.head_table_name || !settings.item_table_name) {
    throw new Error(`${code} must be a configured non-master transaction with head and item tables.`);
  }
  if (order && settings.is_auto_transaction_no !== true) throw new Error(`${code} requires automatic purchase order numbering.`);
}

export const offerKey = (offer) => `${offer.quotation_id}:${offer.quotation_serial}`;

// Integer micro-units avoid floating-point errors when splitting a line between vendors.
function units(value) {
  const number = Number(value);
  const scaled = Math.round(number * 1e6);
  return Number.isFinite(number) && Number.isSafeInteger(scaled) ? scaled : NaN;
}

export function validatePurchaseSelections(lines, quantities) {
  const errors = [];
  const selections = [];
  const vendors = new Map();
  const seen = new Set();
  for (const line of lines || []) {
    let total = 0;
    for (const offer of line.offers || []) {
      const key = offerKey(offer);
      const raw = quantities[key];
      if (raw === undefined || raw === "") continue;
      const quantity = Number(raw);
      const selected = units(raw);
      if (!/^\d+(?:\.\d{1,6})?$/.test(String(raw)) || !Number.isFinite(selected) || quantity <= 0) {
        errors.push(`Line ${line.indent_serial}, quotation ${offer.quotation_id}/${offer.quotation_serial}: enter a positive quantity with at most six decimal places.`);
        continue;
      }
      if (seen.has(key) || offer.eligible !== true || !Number.isFinite(units(offer.available_quantity)) || quantity > Number(offer.available_quantity) + 1e-9) {
        errors.push(`Line ${line.indent_serial}, quotation ${offer.quotation_id}/${offer.quotation_serial}: selection is unavailable or exceeds offer availability.`);
      }
      seen.add(key);
      total += selected;
      selections.push({ quotation_id: offer.quotation_id, quotation_serial: offer.quotation_serial, quantity });
      const head = offer.head_data || {};
      const currency = head.currency || (head.currency_id ? `Currency ID ${head.currency_id}` : "Currency unspecified");
      const vendorKey = JSON.stringify([offer.vendor_id, currency, head.currency_id, head.exchange_rate]);
      const vendor = vendors.get(vendorKey) || { vendorID: offer.vendor_id, name: head.company_name || `Vendor #${offer.vendor_id}`, currency, exchangeRate: head.exchange_rate, subtotal: 0, quantity: 0 };
      vendor.subtotal += quantity * Number(offer.rate);
      vendor.quantity += quantity;
      vendors.set(vendorKey, vendor);
    }
    if (!Number.isSafeInteger(total) || !Number.isFinite(units(line.remaining_quantity)) || total / 1e6 > Number(line.remaining_quantity) + 1e-9) {
      errors.push(`Line ${line.indent_serial}: combined selected quantity exceeds remaining indent quantity (${line.remaining_quantity}).`);
    }
  }
  if (selections.length > 1000) errors.push("Select no more than 1000 quotation lines per comparison.");
  return { errors, selections, vendors: [...vendors.values()] };
}

// Match the existing list row -> SalesForm view navigation, not /details/:id.
export function purchaseDocumentLocation(menus, code, id) {
  const menu = menus.find((entry) => entry.menu_code === code && entry.rights?.can_view === true && entry.parentName && entry.display_title);
  if (!menu || getModuleRoute(menu)) return null;
  const slug = (text) => encodeURIComponent(text.toLowerCase().replace(/\s+/g, "-"));
  return { pathname: `/${slug(menu.parentName)}/${slug(menu.display_title.replace(/\//g, "-"))}/transactions`, state: { transactionCode: code, transactionId: Number(id) } };
}